import { TRPCClientError, type TRPCLink } from '@trpc/client'
import type { AnyRouter } from '@trpc/server'
import { observable, type Unsubscribable } from '@trpc/server/observable'

import { abortAfter } from '@/lib/fetch-with-timeout'
import {
  syncFailureReason,
  SyncRequestError,
  type SyncFailureReason,
} from '@/lib/sync-manager/sync-errors'

/** How long a call may take, session lookup and token refresh included (K42). */
export const REQUEST_TIMEOUT_MS = 60_000

const fail = (reason: SyncFailureReason, message: string) =>
  TRPCClientError.from(new SyncRequestError(reason, message))

/**
 * Fails a call that hasn't settled after `ms` and aborts its request. Without it
 * a hung call (a dead socket after a network handover, a session lookup stuck on
 * the auth lock) kept its collection syncing until the app was reloaded, and every
 * later sync of it, a save's included, waited on it (K42).
 */
export function timeoutLink<TRouter extends AnyRouter>(
  ms = REQUEST_TIMEOUT_MS
): TRPCLink<TRouter> {
  return () =>
    ({ op, next }) =>
      observable((observer) => {
        const timeout = abortAfter(ms, op.signal, () =>
          observer.error(
            fail('timeout', `${op.path}: no answer after ${ms / 1000} s`)
          )
        )
        const settled = () => timeout.dispose()

        const subscription = next({
          ...op,
          signal: timeout.signal,
        }).subscribe({
          next: (value) => observer.next(value),
          error: (error) => {
            settled()
            observer.error(error)
          },
          complete: () => {
            settled()
            observer.complete()
          },
        })

        return () => {
          settled()
          subscription.unsubscribe()
        }
      })
}

/**
 * Where calls get their token, and how a refused one is renewed. Both reject
 * with a SyncRequestError saying why there is no token (offline, no session,
 * refused by the auth server).
 */
export interface SessionSource {
  /** The session's access token; `undefined` when there is no session */
  token(): Promise<string | undefined>
  /** A new access token */
  refresh(): Promise<string>
}

/** Why a session call failed, `fallback` when it didn't say. */
const reasonOf = (error: unknown, fallback: SyncFailureReason) =>
  error instanceof SyncRequestError ? error.reason : fallback

/**
 * Sends each call with the session's token, in the operation's `authToken`
 * context for the terminating link's headers (K44):
 * - no session: the call isn't sent and fails at once with `no_session`, like
 *   being offline; the triggers retry and the session library retries its refresh;
 * - refused (401): the session is refreshed once and the call sent again once; a
 *   refresh that fails on the network fails the call with `refresh_failed`, one
 *   the auth server rejects with `rejected` (the session library then signs out,
 *   which takes the user to the login);
 * - calls `isPublic` names (the login) go through untouched.
 */
export function sessionLink<TRouter extends AnyRouter>(
  session: SessionSource,
  isPublic: (path: string) => boolean
): TRPCLink<TRouter> {
  return () =>
    ({ op, next }) =>
      observable((observer) => {
        if (isPublic(op.path)) return next(op).subscribe(observer)

        let closed = false
        let subscription: Unsubscribable | undefined
        const failWith = (reason: SyncFailureReason, message: string) => {
          if (!closed) observer.error(fail(reason, `${op.path}: ${message}`))
        }

        const send = (token: string, retry: boolean) => {
          subscription = next({
            ...op,
            context: { ...op.context, authToken: token },
          }).subscribe({
            next: (value) => observer.next(value),
            complete: () => observer.complete(),
            error: (error) => {
              if (!retry || syncFailureReason(error) !== 'unauthorized') {
                return observer.error(error)
              }
              session.refresh().then(
                (token) => {
                  if (!closed) send(token, false)
                },
                (cause) =>
                  failWith(
                    reasonOf(cause, 'refresh_failed'),
                    'token refused, refresh failed'
                  )
              )
            },
          })
        }

        session.token().then(
          (token) => {
            if (closed) return
            if (token) send(token, true)
            else failWith('no_session', 'no session, not sent')
          },
          (cause) =>
            failWith(reasonOf(cause, 'no_session'), 'no session, not sent')
        )

        return () => {
          closed = true
          subscription?.unsubscribe()
        }
      })
}
